import {
  BookmarkBorder as BookmarkBorderIcon,
  ChatBubbleOutline as CommentIcon,
  FavoriteBorder as LikeIcon,
  Favorite as LikedIcon,
  MoreHoriz as MoreHorizIcon,
  Repeat as RetweetIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Stack,
  CardMedia,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextIconLabel from 'src/components/TextIconLabel';
import { PostUtil } from 'src/pages/new-post-new/post-util';
import { CommonHelper } from 'src/utils/commonHelper';

const TwitterPostCard = ({
  media_urls,
  description,
  profile,
  like_count = 0,
  reply_count = 0,
  retweet_count = 0,
  bookmark_count = 0,        // Optional
  impression_count = 0,   // Views
  handleFocusComment,
}) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isLiked, setIsLiked] = useState(false); // Local state for demo

  const mediaFiles = media_urls || [];

  const hasManageLikesCommentPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.MANAGE_COMMENTS_LIKES
  );

  const toggleDescription = () => setShowFullDescription(!showFullDescription);


    const getBorderRadius = (i, totalMedia) => {
    const radius = '16px';
    if (totalMedia === 1) return radius;
    if (totalMedia === 2) return i === 0 ? `${radius} 0 0 ${radius}` : `0 ${radius} ${radius} 0`;
    if (totalMedia === 3) {
      if (i === 0) return `${radius} 0 0 0`;
      if (i === 1) return `0 ${radius} 0 0`;
      if (i === 2) return `0 0 ${radius} ${radius}`;
    }
    if (totalMedia >= 4) {
      if (i === 0) return `${radius} 0 0 0`;
      if (i === 1) return `0 ${radius} 0 0`;
      if (i === 2) return `0 0 0 ${radius}`;
      if (i === 3) return `0 0 ${radius} 0`;
    }
    return '0';
  };

  const mediaView = () => {
    let view = [];
    let xWidth = 6;
    let isThreeMedia = false;
    const totalMedia = mediaFiles.length;
    const remainingMediaCount = totalMedia > 4 ? totalMedia - 4 : 0;

    switch (totalMedia) {
      case 1:
        xWidth = 12;
        break;
      case 2:
      case 4:
      default:
        xWidth = 6;
        break;
      case 3:
        isThreeMedia = true;
        break;
    }

    if (isThreeMedia) {
      const gridItemStyle = { position: 'relative', overflow: 'hidden' };
      view = (
        <Grid container spacing={0.2} sx={{ position: 'relative' }}>
          <Grid item xs={6} sx={gridItemStyle}>
            {renderMedia(mediaFiles[0], 0, 300)}
          </Grid>
          <Grid item xs={6}>
            <Grid container direction="column" spacing={0.2}>
              {[1, 2].map((i) => (
                <Grid item xs={12} key={i} sx={gridItemStyle}>
                  {renderMedia(mediaFiles[i], i, 150)}
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      );
    } else {
      view = mediaFiles?.slice(0, 4).map((media, i) => {
        const gridItemStyle = {
          maxWidth: 500,
          maxHeight: 250,
          overflow: 'hidden',
          position: 'relative',
          borderRadius: getBorderRadius(i, totalMedia),
        };
        return (
          <Grid
            item
            xs={xWidth}
            key={i}
            sx={gridItemStyle}
          >
            {renderMedia(media, i, 250, remainingMediaCount)}
          </Grid>
        );
      });
    }

    return (
      <Grid container spacing={0.2} sx={{ position: 'relative' }}>
        {view}
      </Grid>
    );
  };
  const renderMedia = (media, index, height, remainingCount = 0) => {
    const src = media?.media_urls || media;
    const isVideo = media?.media_type.toLowerCase() === PostUtil.MediaType.VIDEO;
    const isImage = media?.media_type.toLowerCase() === PostUtil.MediaType.PHOTO

    const mediaStyle = {
      width: '100%',
      height,
      objectFit: 'cover',
      borderRadius: 'inherit',
    };

    let borderRadiusForItem = 'inherit';
    if (mediaFiles.length === 3 && index === 0) borderRadiusForItem = '12px 0 0 12px';
    if (mediaFiles.length === 3 && index === 1) borderRadiusForItem = '0 12px 0 0';
    if (mediaFiles.length === 3 && index === 2) borderRadiusForItem = '0 0 12px 0';

    return (
      <>
        {isImage ? (
          <img
            src={src}
            alt={`media-${index}`}
            style={{ ...mediaStyle, borderRadius: borderRadiusForItem }}
          />
        ) : isVideo ? (
         <video
            src={src}
            controls
            style={{ ...mediaStyle, borderRadius: borderRadiusForItem }}
          />
        ) : null}

        {index === 3 && remainingCount > 0 && (
          <Typography
            variant="h2"
            color="white"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            +{remainingCount}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Card
      sx={{
        maxWidth: 500,
        minWidth: isLargeScreen ? 460 : 380,
        boxShadow: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            src={profile?.profile_image || profile?.connected_profile_image}
            alt={profile?.profile_name}
            sx={{ width: 48, height: 48 }}
          />
        }
        title={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography fontWeight={700} variant="subtitle1">
              {profile?.profile_name || profile?.connected_profile_name}
            </Typography>
            {profile?.verified && <span style={{ color: '#1d9bf0' }}>✓</span>}
          </Stack>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            @{profile?.username || profile?.profile_name?.toLowerCase()} ·{' '}
            {profile?.date ? CommonHelper.DateTimeFromCurrentTime(profile.date) : translate('preview.just_now')}
          </Typography>
        }
        action={<IconButton><MoreHorizIcon /></IconButton>}
      />

      <CardContent sx={{ pt: 0, pb: 1 }}>
        <Typography
          sx={{
            whiteSpace: 'pre-line',
            fontSize: '1rem',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: showFullDescription ? 'unset' : 5,
            overflow: 'hidden',
          }}
        >
          {CommonHelper.ParseDescription(description)}
        </Typography>

        {(description?.length > 200 || showFullDescription) && (
          <Typography
            onClick={toggleDescription}
            sx={{
              color: '#1d9bf0',
              fontWeight: 500,
              cursor: 'pointer',
              mt: 0.5,
              display: 'inline-block',
            }}
          >
            {showFullDescription ? 'Show less' : 'Show more'}
          </Typography>
        )}
      </CardContent>
       <CardMedia sx={{ pl: 2, pr: 2 }}>
          {mediaFiles.length > 0 ? mediaView() : null}
        </CardMedia>

      {/* Engagement Bar - Exact X style */}
      <CardActions sx={{ px: 2, pb: 1, pt: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%', color: '#71767b' }}
        >
          {/* Reply */}
          <TextIconLabel
            aria-label="reply"
            value={reply_count > 0 ? CommonHelper.formatNumber(reply_count) : ''}
            icon={
              <IconButton
                onClick={handleFocusComment}
                disabled={!hasManageLikesCommentPermission}
                sx={{ color: '#71767b', '&:hover': { color: '#1d9bf0', bgcolor: 'rgba(29, 155, 240, 0.1)' } }}
              >
                <CommentIcon />
              </IconButton>
            }
          />

          {/* Repost / Retweet */}
          <TextIconLabel
            aria-label="retweet"
            value={retweet_count > 0 ? CommonHelper.formatNumber(retweet_count) : ''}
            icon={
              <IconButton
                sx={{ color: '#71767b', '&:hover': { color: '#00ba7c', bgcolor: 'rgba(0, 186, 124, 0.1)' } }}
              >
                <RetweetIcon />
              </IconButton>
            }
          />

          {/* Like */}
          <TextIconLabel
            aria-label="like"
            value={like_count > 0 ? CommonHelper.formatNumber(like_count) : ''}
            icon={
              <IconButton
                onClick={() => setIsLiked(!isLiked)}
                sx={{
                  color: isLiked ? '#f91880' : '#71767b',
                  '&:hover': { color: '#f91880', bgcolor: 'rgba(249, 24, 128, 0.1)' },
                }}
              >
                {isLiked ? <LikedIcon /> : <LikeIcon />}
              </IconButton>
            }
          />

          {/* Bookmark */}
          <TextIconLabel
            aria-label="bookmark"
             value={bookmark_count > 0 ? CommonHelper.formatNumber(bookmark_count) : ''}
            icon={
              <IconButton
                sx={{ color: '#71767b', '&:hover': { color: '#1d9bf0', bgcolor: 'rgba(29, 155, 240, 0.1)' } }}
              >
                <BookmarkBorderIcon />
              </IconButton>
            }
          />

          {/* Share (more options) */}
          <IconButton
            sx={{ color: '#71767b', '&:hover': { color: '#1d9bf0', bgcolor: 'rgba(29, 155, 240, 0.1)' } }}
          >
            <MoreHorizIcon />
          </IconButton>
        </Stack>
      </CardActions>
    </Card>
  );
};

export default TwitterPostCard;